import { format, isWeekend, getDay, getMonth } from 'date-fns';
import {
    PricingConfiguration,
    CalculatedPrice,
    DayCalculation,
    OTAChannel,
    LOSCategory,
    PAXCategory,
    PricingRule,
} from '../types';

export class PricingEngine {
    private config: PricingConfiguration;

    constructor(config: PricingConfiguration) {
        this.config = config;
    }

    updateConfiguration(config: PricingConfiguration) {
        this.config = config;
    }

    calculateDayPrices(date: Date): DayCalculation {
        const dateStr = format(date, 'yyyy-MM-dd');
        const basePrice = this.getBasePriceForDate(date);

        const calculation: DayCalculation = {
            date: dateStr,
            prices: {},
        };

        // Calculate for each enabled channel
        const enabledChannels = this.config.otaChannels.filter(channel => channel.enabled);

        for (const channel of enabledChannels) {
            calculation.prices[channel.id] = {};

            for (const losCategory of this.config.losCategories) {
                calculation.prices[channel.id][losCategory.id] = {};

                for (const paxCategory of this.config.paxCategories) {
                    calculation.prices[channel.id][losCategory.id][paxCategory.id] =
                        this.calculatePrice(date, basePrice, channel, losCategory, paxCategory);
                }
            }
        }

        return calculation;
    }

    private getBasePriceForDate(date: Date, losId?: string, paxId?: string): number {
        const dateStr = format(date, 'yyyy-MM-dd');

        // First check for cell-specific override
        if (losId && paxId) {
            const cellOverride = this.config.priceOverrides.find(
                o => o.date === dateStr && o.losId === losId && o.paxId === paxId
            );
            if (cellOverride) {
                return cellOverride.basePrice;
            }
        }

        // Then check for day-level override
        const dayOverride = this.config.priceOverrides.find(
            o => o.date === dateStr && !o.losId && !o.paxId
        );

        return dayOverride ? dayOverride.basePrice : this.config.priceConfig.basePrice;
    }

    private calculatePrice(
        date: Date,
        basePrice: number,
        channel: OTAChannel,
        losCategory: LOSCategory,
        paxCategory: PAXCategory
    ): CalculatedPrice {
        // Get cell-specific price if available
        const cellSpecificBasePrice = this.getBasePriceForDate(date, losCategory.id, paxCategory.id);

        // Use cell-specific price if available, otherwise use the provided basePrice
        let currentPrice = cellSpecificBasePrice;
        const appliedRules: string[] = [];
        const ruleBreakdown: { name: string; percent: number }[] = [];

        // Apply LOS modifier (now using sellingPriceDifference and sellingPriceDifferenceType)
        if (losCategory.sellingPriceDifferenceType === 'percent') {
            currentPrice *= (1 + (losCategory.sellingPriceDifference || 0) / 100);
            if (losCategory.sellingPriceDifference) {
                appliedRules.push(`LOS: ${losCategory.name} (${losCategory.sellingPriceDifference > 0 ? '+' : ''}${losCategory.sellingPriceDifference}%)`);
                ruleBreakdown.push({ name: `LOS: ${losCategory.name}`, percent: losCategory.sellingPriceDifference });
            }
        } else if (losCategory.sellingPriceDifferenceType === 'absolute') {
            currentPrice += losCategory.sellingPriceDifference || 0;
            if (losCategory.sellingPriceDifference) {
                const percentDiff = ((losCategory.sellingPriceDifference || 0) / currentPrice) * 100;
                appliedRules.push(`LOS: ${losCategory.name} (${losCategory.sellingPriceDifference > 0 ? '+' : ''}€${(losCategory.sellingPriceDifference || 0).toFixed(2)})`);
                ruleBreakdown.push({ name: `LOS: ${losCategory.name}`, percent: percentDiff });
            }
        }

        // Apply PAX modifier (sellingPriceDifference and sellingPriceDifferenceType)
        if (paxCategory.sellingPriceDifferenceType === 'percent') {
            currentPrice *= (1 + (paxCategory.sellingPriceDifference || 0) / 100);
            if (paxCategory.sellingPriceDifference) {
                appliedRules.push(`PAX: ${paxCategory.name} (${paxCategory.sellingPriceDifference > 0 ? '+' : ''}${paxCategory.sellingPriceDifference}%)`);
                ruleBreakdown.push({ name: `PAX: ${paxCategory.name}`, percent: paxCategory.sellingPriceDifference });
            }
        } else if (paxCategory.sellingPriceDifferenceType === 'absolute') {
            currentPrice += paxCategory.sellingPriceDifference || 0;
            if (paxCategory.sellingPriceDifference) {
                const percentDiff = ((paxCategory.sellingPriceDifference || 0) / currentPrice) * 100;
                appliedRules.push(`PAX: ${paxCategory.name} (${paxCategory.sellingPriceDifference > 0 ? '+' : ''}€${(paxCategory.sellingPriceDifference || 0).toFixed(2)})`);
                ruleBreakdown.push({ name: `PAX: ${paxCategory.name}`, percent: percentDiff });
            }
        }

        // Apply pricing rules
        for (const rule of this.config.pricingRules) {
            if (rule.enabled && this.doesRuleApply(rule, date)) {
                currentPrice *= (1 + rule.priceModifier / 100);
                appliedRules.push(`${rule.name} (${rule.priceModifier > 0 ? '+' : ''}${rule.priceModifier}%)`);
                ruleBreakdown.push({ name: rule.name, percent: rule.priceModifier });
            }
        }

        // Store the pre-channel-fee price for agency/owner calculations
        const preChannelFeePrice = currentPrice;

        // Apply channel margin
        if (channel.id !== 'direct') {
            currentPrice *= (1 + channel.marginPercent / 100);
            appliedRules.push(`${channel.name} margin (${channel.marginPercent > 0 ? '+' : ''}${channel.marginPercent}%)`);
        }

        // Apply business model calculations using the pre-channel-fee price
        const finalPrice = this.applyBusinessModel(currentPrice);

        // Validate against min/max
        const exceedsMax = finalPrice > this.config.priceConfig.maxPrice;
        const belowMin = this.config.priceConfig.minPrice ?
            finalPrice < this.config.priceConfig.minPrice : false;

        return {
            basePrice: cellSpecificBasePrice,
            finalPrice: Math.round(finalPrice * 100) / 100, // Round to 2 decimal places
            isValid: !exceedsMax && !belowMin,
            exceedsMax,
            belowMin,
            appliedRules,
            ruleBreakdown,
            preChannelFeePrice: Math.round(preChannelFeePrice * 100) / 100, // Add pre-channel-fee price
        };
    }

    private doesRuleApply(rule: PricingRule, date: Date): boolean {
        // Check day of week filter
        if (rule.dayOfWeekFilter && rule.dayOfWeekFilter.length > 0) {
            const dayOfWeek = getDay(date);
            if (!rule.dayOfWeekFilter.includes(dayOfWeek)) {
                return false;
            }
        }

        // Check month filter
        if (rule.monthFilter && rule.monthFilter.length > 0) {
            const month = getMonth(date) + 1; // getMonth returns 0-11, we want 1-12
            if (!rule.monthFilter.includes(month)) {
                return false;
            }
        }

        // Check date range filter
        if (rule.dateRangeFilter) {
            const dateTime = date.getTime();
            const startTime = rule.dateRangeFilter.start.getTime();
            const endTime = rule.dateRangeFilter.end.getTime();
            if (dateTime < startTime || dateTime > endTime) {
                return false;
            }
        }

        return true;
    }

    private applyBusinessModel(price: number): number {
        const { businessModel } = this.config;

        switch (businessModel.model) {
            case 'absolute_same':
                // Owner gets fixed fee, agency gets the rest
                return price;

            case 'absolute_different':
                // Owner fee varies with rate plan
                return price;

            case 'relative_percentage':
                // Output price should not be changed by owner payout percent
                return price;

            default:
                return price;
        }
    }

    calculateMonthlyPrices(year: number, month: number): DayCalculation[] {
        const calculations: DayCalculation[] = [];
        const daysInMonth = new Date(year, month, 0).getDate();

        for (let day = 1; day <= daysInMonth; day++) {
            const date = new Date(year, month - 1, day);
            calculations.push(this.calculateDayPrices(date));
        }

        return calculations;
    }

    /**
     * Returns an object with both the regular max price and the special day max price suggestion.
     * Usage: const { regular, specialDay } = pricingEngine.suggestMaxPrice();
     */
    suggestMaxPrice(): { regular: number, specialDay: number } {
        let base = this.config.priceConfig.basePrice;
        // Buffer (immediately after base price)
        const bufferPercent = this.config.priceConfig.bufferPercent ?? 20;
        let regularMax = base * (1 + bufferPercent / 100);
        let specialDayMax = base * (1 + bufferPercent / 100);

        // Highest LOS modifier (percent or absolute)
        let maxLOS: LOSCategory | undefined = undefined;
        for (const los of this.config.losCategories) {
            if (!maxLOS) {
                maxLOS = los;
            } else if (los.sellingPriceDifferenceType === maxLOS.sellingPriceDifferenceType) {
                if ((los.sellingPriceDifference ?? 0) > (maxLOS.sellingPriceDifference ?? 0)) {
                    maxLOS = los;
                }
            } else if (los.sellingPriceDifferenceType === 'percent' && maxLOS.sellingPriceDifferenceType === 'absolute') {
                const percentValue = regularMax * ((los.sellingPriceDifference ?? 0) / 100);
                if (percentValue > (maxLOS.sellingPriceDifference ?? 0)) {
                    maxLOS = los;
                }
            } else if (los.sellingPriceDifferenceType === 'absolute' && maxLOS.sellingPriceDifferenceType === 'percent') {
                const percentValue = regularMax * ((maxLOS.sellingPriceDifference ?? 0) / 100);
                if ((los.sellingPriceDifference ?? 0) > percentValue) {
                    maxLOS = los;
                }
            }
        }
        if (maxLOS) {
            if (maxLOS.sellingPriceDifferenceType === 'percent') {
                regularMax *= 1 + ((maxLOS.sellingPriceDifference ?? 0) / 100);
                specialDayMax *= 1 + ((maxLOS.sellingPriceDifference ?? 0) / 100);
            } else if (maxLOS.sellingPriceDifferenceType === 'absolute') {
                regularMax += maxLOS.sellingPriceDifference ?? 0;
                specialDayMax += maxLOS.sellingPriceDifference ?? 0;
            }
        }

        // Highest PAX modifier (always percent)
        const maxPAX = this.config.paxCategories.reduce((max, pax) => {
            if ((pax.sellingPriceDifferenceType === 'percent' || !pax.sellingPriceDifferenceType) && (pax.sellingPriceDifference ?? 0) > (max ?? 0)) {
                return pax.sellingPriceDifference ?? 0;
            }
            return max ?? 0;
        }, 0);
        regularMax *= 1 + maxPAX / 100;
        specialDayMax *= 1 + maxPAX / 100;

        // --- Pricing Rules ---
        // Separate rules into categories
        const rules = this.config.pricingRules.filter(rule => rule.enabled && rule.priceModifier > 0);
        const seasonRules = rules.filter(rule => (rule.dateRangeFilter || rule.monthFilter) && !(rule.name.toLowerCase().includes('holiday') || rule.name.toLowerCase().includes('special')));
        const repeatingDayRules = rules.filter(rule => rule.dayOfWeekFilter && !rule.dateRangeFilter && !rule.monthFilter && !(rule.name.toLowerCase().includes('holiday') || rule.name.toLowerCase().includes('special')));
        const specialDayRules = rules.filter(rule => rule.dateRangeFilter && !rule.dayOfWeekFilter && !rule.monthFilter && (rule.name.toLowerCase().includes('holiday') || rule.name.toLowerCase().includes('special')));
        // Exclude discounts (negative modifiers)
        // (already filtered above by rule.priceModifier > 0)

        // Highest season rule
        let maxSeasonRule = seasonRules.length > 0 ? seasonRules.reduce((a, b) => (a.priceModifier > b.priceModifier ? a : b)) : undefined;
        if (maxSeasonRule) {
            regularMax *= 1 + maxSeasonRule.priceModifier / 100;
        }

        // Highest repeating day rule
        let maxRepeatingDayRule = repeatingDayRules.length > 0 ? repeatingDayRules.reduce((a, b) => (a.priceModifier > b.priceModifier ? a : b)) : undefined;
        if (maxRepeatingDayRule) {
            regularMax *= 1 + maxRepeatingDayRule.priceModifier / 100;
        }

        // Highest special day rule (not compounded with others)
        let maxSpecialDayRule = specialDayRules.length > 0 ? specialDayRules.reduce((a, b) => (a.priceModifier > b.priceModifier ? a : b)) : undefined;
        if (maxSpecialDayRule) {
            specialDayMax = base * (1 + bufferPercent / 100);
            if (maxLOS) {
                if (maxLOS.sellingPriceDifferenceType === 'percent') {
                    specialDayMax *= 1 + ((maxLOS.sellingPriceDifference ?? 0) / 100);
                } else if (maxLOS.sellingPriceDifferenceType === 'absolute') {
                    specialDayMax += maxLOS.sellingPriceDifference ?? 0;
                }
            }
            specialDayMax *= 1 + maxPAX / 100;
            specialDayMax *= 1 + maxSpecialDayRule.priceModifier / 100;
        }

        // Highest channel margin
        const maxChannelMargin = Math.max(...this.config.otaChannels.filter(channel => channel.enabled).map(channel => channel.marginPercent));
        regularMax *= 1 + maxChannelMargin / 100;
        specialDayMax *= 1 + maxChannelMargin / 100;

        return {
            regular: Math.ceil(regularMax),
            specialDay: Math.ceil(specialDayMax),
        };
    }
} 